// noinspection GraphQLUnresolvedReference

import type { GraphqlClientOptionsProps } from '../../../base/api/graphqlClient.ts'
import { graphqlClient, toGraphQlError } from '../../../base/api/graphqlClient.ts'
import { invalidateQuery, type InvalidateQueryOptions, useApiQuery } from '../../../base/api/queryUtils.ts'
import type { QueryOpts } from '../../../base/types/reactQuery.ts'
import { gqlAst } from '../../../utils/gql.ts'
import { AreaFragmentAst } from '../fragments/areaFragment.ts'
import { AreaProps } from '../interfaces/area.ts'

const gqlQuery = /* GraphQL */ gqlAst`
  ${AreaFragmentAst}

  query AREA_LISTADO($codigoSucursal: Int!) {
    areaListado(codigoSucursal: $codigoSucursal) {
      ...areasFields
    }
  }
`

export const KEY_AREA_LISTADO = 'area-listado'

export interface ApiAreaListadoParams {
  codigoSucursal: number
}

/**
 * Listado de areas de local
 * @param params codigo sucursal de la sesión
 * @param options Headers adicionales y señal de cancelación de la petición.
 */
export const apiAreaListado = async (
  params: ApiAreaListadoParams,
  options: GraphqlClientOptionsProps = {},
): Promise<AreaProps[]> => {
  try {
    const data = await graphqlClient.request<{ areaListado: AreaProps[] }>({
      document: gqlQuery,
      variables: { ...params },
      requestHeaders: options.headers,
      signal: options.signal,
    })
    return data.areaListado
  } catch (error) {
    throw toGraphQlError(error, options.signal)
  }
}

/**
 * Invalida el listado de pedidos desde cualquier lugar (ej. onSuccess de mutaciones),
 * sin necesidad de tener montado el listado.
 * @example invalidatePedidoListado()
 * @example invalidatePedidoListado({ extraQueryKeys: [entidad] })
 */
export const invalidateAreaListado = (options?: InvalidateQueryOptions) =>
  invalidateQuery(KEY_AREA_LISTADO, options)

/**
 * Hook para consultar el listado de áreas de una sucursal.
 * queryKey: [KEY_AREA_LISTADO, codigoSucursal, ...(options.queryKeys ?? [])]
 * Para invalidar sin montar el hook, usar `invalidateAreaListado()`.
 * @param params codigo sucursal de la sesión
 * @param options Opciones de React Query con soporte de queryKeys adicionales.
 */
export const useAreaListado = (params: ApiAreaListadoParams, options: QueryOpts<AreaProps[]> = {}) =>
  useApiQuery(KEY_AREA_LISTADO, (ctx) => apiAreaListado(params, { signal: ctx.signal }), {
    refetchOnWindowFocus: false,
    ...options,
    queryKeys: [params.codigoSucursal, ...(options.queryKeys ?? [])],
  })
